import React, { Suspense, useEffect, useRef } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { LoadScript } from '@react-google-maps/api'
import { ToastContainer } from 'react-toastify'
import { GOOGLE_MAPS_API_KEY } from '@/constant/constant.ts'
import { useMapsConfig } from '@/hooks/useMapsConfig'
import { queryClient } from '@/api/queryClient.ts'
import FullPageLoading from '@/component/util/FullPageLoading'
import { MapProvider, MapType, useMapType } from '@/component/map/MapContext'
import BaiduMapLoader from '@/component/map/baidu/BaiduMapLoader'
import { useAuth } from '@/hooks/useAuth'
import 'react-toastify/dist/ReactToastify.css'

interface AppProvidersProps {
    children: React.ReactNode
}

const MAP_SDK_TIMEOUT = 10 * 1000

const MapLoaderSwitch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { region, language } = useMapsConfig()
    const { mapType, failedMapType, setFailedMapType } = useMapType()
    const { isLoggedIn } = useAuth()
    const loadedMapType = useRef<MapType | null>(null)

    useEffect(() => {
        if (!isLoggedIn || loadedMapType.current === mapType) return
        const timer = setTimeout(() => {
            if (loadedMapType.current !== mapType) setFailedMapType(mapType)
        }, MAP_SDK_TIMEOUT)
        return () => clearTimeout(timer)
    }, [isLoggedIn, mapType])

    const onLoad = () => {
        loadedMapType.current = mapType
        setFailedMapType(null)
    }
    const onError = () => setFailedMapType(mapType)

    /*
     * 지도 SDK 로더는 로그인 세션 동안 유지한다.
     * 경로별로 LoadScript를 제거했다가 다시 만들면 SDK cleanup과 재주입이 충돌해
     * 건강관리 → 대시보드/회원관리 이동 시 loadingElement에 머무를 수 있다.
     *
     * SDK 를 못 불러오면(네트워크 차단·시간 초과) 로딩 화면 대신 앱을 지도 없이 띄우고 지도 자리에 안내를 보인다.
     * 로더는 loadingElement 와 children 을 같은 자리에 그리므로, 늦게라도 불러오면 앱을 다시 만들지 않고 이어진다.
     */
    if (!isLoggedIn) {
        return <>{children}</>
    }

    const loadingElement = failedMapType === mapType ? children : <FullPageLoading />

    if (mapType === 'baidu') {
        return (
            <BaiduMapLoader loadingElement={loadingElement} onLoad={onLoad} onError={onError}>
                {children}
            </BaiduMapLoader>
        )
    }

    return (
        <LoadScript
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            region={region}
            language={language}
            loadingElement={loadingElement}
            onLoad={onLoad}
            onError={onError}
        >
            {children}
        </LoadScript>
    )
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
    return (
        <Suspense fallback={<FullPageLoading />}>
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true
                }}>
                <MapProvider>
                    <MapLoaderSwitch>
                        <div className="font-sans">
                            <ToastContainer
                                position="bottom-right"
                                autoClose={3000}
                                newestOnTop={false}
                                closeOnClick
                                rtl={false}
                                pauseOnFocusLoss={false}
                                draggable
                                pauseOnHover
                                theme="light"
                                stacked
                            />
                            <QueryClientProvider client={queryClient}>
                                {children}
                            </QueryClientProvider>
                        </div>
                    </MapLoaderSwitch>
                </MapProvider>
            </BrowserRouter>
        </Suspense>
    )
}
